// components/SchemaBuilder.js

import React, { useState, useCallback, useEffect } from 'react';
import { produce } from 'immer';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GripVertical,
  PlusCircle,
  Trash2,
  Edit,
  Copy,
  Settings2,
  Type,
  Hash,
  ToggleRight,
  Braces,
  Brackets,
  CheckSquare,
  Save,
} from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import toast from 'react-hot-toast';

// --- Utility for Class Names ---
const cn = (...inputs) => twMerge(clsx(inputs));

// --- Constants ---
const DATA_TYPES = [
  { value: 'String', label: 'String', icon: <Type size={16} /> },
  { value: 'Number', label: 'Number', icon: <Hash size={16} /> },
  { value: 'Boolean', label: 'Boolean', icon: <ToggleRight size={16} /> },
  { value: 'Object', label: 'Object', icon: <Braces size={16} /> },
  { value: 'Array', label: 'Array', icon: <Brackets size={16} /> },
];

const generateId = () =>
  `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const createNewField = (dataType = 'String') => {
  let defaultValue;
  switch (dataType) {
    case 'String':
      defaultValue = '';
      break;
    case 'Number':
      defaultValue = 0;
      break;
    case 'Boolean':
      defaultValue = false;
      break;
    case 'Array':
      defaultValue = [];
      break;
    case 'Object':
      defaultValue = {};
      break;
    default:
      defaultValue = null;
  }

  return {
    id: generateId(),
    fieldName: '',
    dataType: dataType,
    required: false,
    defaultValue: defaultValue,
    description: '',
    children: [],
    arrayItemType: 'String',
  };
};

// A recursive helper to find and update a field by path (for delete/duplicate)
const findAndApply = (nodes, path, callback) => {
  const parts = path
    .split('.')
    .map((p) => (isNaN(parseInt(p)) ? p : parseInt(p)));
  let current = nodes;
  for (let i = 0; i < parts.length - 1; i++) {
    current = current[parts[i]];
  }
  callback(current, parts[parts.length - 1]);
};

// ============================================================================
// --- UI Components ---
// ============================================================================

const Badge = ({ children, className }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium',
      className,
    )}
  >
    {children}
  </span>
);

const IconButton = ({ children, onClick, className, tooltip }) => (
  <button
    onClick={onClick}
    className={cn(
      'group relative rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-100',
      className,
    )}
  >
    {children}
    {tooltip && (
      <span className="absolute bottom-full mb-2 hidden w-max rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
        {tooltip}
      </span>
    )}
  </button>
);

// ============================================================================
// --- Field Editor Sheet Component ---
// ============================================================================

const FieldEditor = ({
  isOpen,
  onClose,
  field,
  onUpdate,
  path,
  context = 'output',
}) => {
  if (!field) return null;

  // const handleFieldChange = (e) => {
  //     const { name, value, type, checked } = e.target;
  //     const newValue = type === 'checkbox' ? checked : value;
  //     onUpdate(path, { ...field, [name]: newValue });
  // };
  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newFieldValue = type === 'checkbox' ? checked : value;

    // --- THIS IS THE FIX ---
    // If we're updating the defaultValue for a Number field,
    // we must explicitly convert the string value to a number.
    if (name === 'defaultValue' && field.dataType === 'Number') {
      // parseFloat is safer than parseInt as it handles decimals.
      // If the value is empty or invalid, it defaults to 0.
      newFieldValue = parseFloat(newFieldValue) || 0;
    }

    // For Booleans, the value from the select is a string "true" or "false"
    if (name === 'defaultValue' && field.dataType === 'Boolean') {
      newFieldValue = newFieldValue === 'true';
    }
    // --- END OF FIX ---

    const updatedField = { ...field, [name]: newFieldValue };

    // When dataType changes, reset the defaultValue to the new type's default
    if (name === 'dataType') {
      switch (newFieldValue) {
        case 'String':
          updatedField.defaultValue = '';
          break;
        case 'Number':
          updatedField.defaultValue = 0;
          break;
        case 'Boolean':
          updatedField.defaultValue = false;
          break;
        case 'Array':
          updatedField.defaultValue = [];
          break;
        case 'Object':
          updatedField.defaultValue = {};
          break;
        default:
          updatedField.defaultValue = null;
      }
    }
    onUpdate(path, updatedField);
  };

  const hasDefaultValue = ['String', 'Number', 'Boolean'].includes(
    field.dataType,
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/50"
          />

          {/* Sheet */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-slate-800 p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-700 pb-4">
              <h2 className="text-xl font-semibold text-white">Edit Field</h2>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-white"
              >
                &times;
              </button>
            </div>

            <div className="flex-grow space-y-6 overflow-y-auto py-6">
              <div>
                <label
                  htmlFor="fieldName"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Field Name
                </label>
                <input
                  type="text"
                  name="fieldName"
                  value={field.fieldName}
                  onChange={handleFieldChange}
                  placeholder="e.g., user_id"
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* NEW: Description Textarea */}
              <div>
                <label
                  htmlFor="description"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Description
                </label>
                <textarea
                  name="description"
                  value={field.description || ''}
                  onChange={handleFieldChange}
                  placeholder="A brief description of this field..."
                  rows={3}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label
                  htmlFor="dataType"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Data Type
                </label>
                <select
                  name="dataType"
                  value={field.dataType}
                  onChange={handleFieldChange}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {DATA_TYPES.map((dt) => (
                    <option key={dt.value} value={dt.value}>
                      {dt.label}
                    </option>
                  ))}
                </select>
              </div>
              {field.dataType === 'Array' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-l-2 border-slate-700 pl-4"
                >
                  <label
                    htmlFor="itemsType"
                    className="mb-2 block text-sm font-medium text-slate-300"
                  >
                    Items Type
                  </label>
                  <select
                    name="arrayItemType"
                    value={field.arrayItemType}
                    onChange={handleFieldChange}
                    className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {DATA_TYPES.map((dt) => (
                      <option key={dt.value} value={dt.value}>
                        {dt.label}
                      </option>
                    ))}
                  </select>
                </motion.div>
              )}
              {/* ⭐ NEW: Default/Starting Value Input */}
              {hasDefaultValue && (
                <div>
                  <label
                    htmlFor="defaultValue"
                    className="mb-2 block text-sm font-medium text-slate-300"
                  >
                    {context === 'globalState'
                      ? 'Starting Value'
                      : 'Default Value'}
                  </label>
                  {field.dataType === 'String' && (
                    <input
                      type="text"
                      name="defaultValue"
                      value={field.defaultValue}
                      onChange={handleFieldChange}
                      className="... w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2"
                    />
                  )}
                  {field.dataType === 'Number' && (
                    <input
                      type="number"
                      name="defaultValue"
                      value={field.defaultValue}
                      onChange={handleFieldChange}
                      className="... w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2"
                    />
                  )}
                  {field.dataType === 'Boolean' && (
                    <select
                      name="defaultValue"
                      value={field.defaultValue}
                      onChange={handleFieldChange}
                      className="... w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2"
                    >
                      <option value={false}>False</option>
                      <option value={true}>True</option>
                    </select>
                  )}
                </div>
              )}
              {/* ⭐ UPDATED: "Required" toggle is now conditional */}
              {context === 'output' && (
                <div className="flex items-center justify-between rounded-lg bg-slate-900/50 p-3">
                  <label
                    htmlFor="required"
                    className="text-sm font-medium text-slate-300"
                  >
                    Required
                  </label>

                  <input
                    id="required"
                    type="checkbox"
                    name="required"
                    className="peer sr-only"
                    checked={field.required}
                    onChange={handleFieldChange}
                  />
                  <div className="peer h-6 w-11 rounded-full bg-slate-600 after:absolute after:left-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-slate-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-indigo-600" />
                </div>
              )}
              {context === 'globalState' && (
                <div className="rounded-lg bg-slate-900/50 p-3 text-center text-sm text-slate-400">
                  All global state fields are required by default.
                </div>
              )}
            </div>

            <div className="border-t border-slate-700 pt-4">
              <button
                onClick={onClose}
                className="w-full rounded-lg bg-indigo-600 px-4 py-2 font-bold text-white transition-colors hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// --- Field Row Component (With Fixed-Height Placeholder) ---
// ============================================================================
// ============================================================================
// --- Field Row Component (With fix for orphaned children) ---
// ============================================================================
const FieldRow = React.memo(({ field, path, isDragging, ...props }) => {
  const {
    onDuplicate,
    onDelete,
    onAddField,
    onEdit,
    innerRef,
    draggableProps,
    dragHandleProps,
  } = props;
  const isNested =
    field.dataType === 'Object' ||
    (field.dataType === 'Array' && field.arrayItemType === 'Object');
  const childrenPath = `${path}.children`;
  const dataTypeInfo = DATA_TYPES.find((dt) => dt.value === field.dataType);
  const arrayItemTypeInfo = DATA_TYPES.find(
    (dt) => dt.value === field.arrayItemType,
  );

  return (
    <div className="w-full">
      <div
        ref={innerRef}
        {...draggableProps}
        className={cn(
          'group flex items-start gap-2 rounded-lg bg-slate-800 p-2 transition-colors hover:bg-slate-700/50',
          isDragging && 'opacity-90 shadow-2xl',
        )}
      >
        <div className="cursor-grab pt-1 text-slate-500" {...dragHandleProps}>
          <GripVertical size={20} />
        </div>
        {/* eslint-disable-next-line */}
        <div
          className="flex flex-grow cursor-pointer flex-col gap-1"
          onClick={() => onEdit(path)}
        >
          <div className="flex items-center gap-3">
            <span className="min-w-[150px] truncate font-mono text-white">
              {field.fieldName || (
                <span className="text-slate-500">untitled_field</span>
              )}
            </span>
            <Badge className="bg-slate-700 text-slate-300">
              {dataTypeInfo?.icon}
              <span>{dataTypeInfo?.label}</span>
              {field.dataType === 'Array' && (
                <span className="ml-1.5 flex items-center gap-1.5 border-l border-slate-600 pl-1.5">
                  {arrayItemTypeInfo?.icon}
                  {arrayItemTypeInfo?.label}
                </span>
              )}
            </Badge>
            {field.required && (
              <Badge className="bg-rose-500/10 text-rose-400">
                <CheckSquare size={14} />
                Required
              </Badge>
            )}
          </div>
          {field.description && (
            <p className="pr-2 text-xs text-slate-400">{field.description}</p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100">
          <IconButton onClick={() => onEdit(path)} tooltip="Edit">
            <Edit size={16} />
          </IconButton>
          <IconButton onClick={() => onDuplicate(path)} tooltip="Duplicate">
            <Copy size={16} />
          </IconButton>
          <IconButton
            onClick={() => onDelete(path)}
            className="hover:text-rose-500"
            tooltip="Delete"
          >
            <Trash2 size={16} />
          </IconButton>
        </div>
      </div>

      {/* FINAL FIX: We now hide the children's container when the parent is being dragged. */}
      <div className={cn(isDragging && 'hidden')}>
        {isNested && (
          <div className="ml-6 mt-2 border-l-2 border-slate-700 pl-4 pt-2">
            <Droppable droppableId={childrenPath} type="field">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="min-h-[40px] space-y-2"
                >
                  {field.children.map((child, index) => (
                    <Draggable
                      key={child.id}
                      draggableId={child.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <FieldRow
                          {...props}
                          field={child}
                          path={`${childrenPath}.${index}`}
                          innerRef={provided.innerRef}
                          draggableProps={provided.draggableProps}
                          dragHandleProps={provided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
            <button
              onClick={() => onAddField(path)}
              className="mt-3 flex items-center gap-2 text-sm text-indigo-400 transition-colors hover:text-indigo-300"
            >
              <PlusCircle size={16} />
              <span>Add Nested Field</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

FieldRow.displayName = 'FieldRow';

// ============================================================================
// --- Main Schema Builder Component ---
// ============================================================================

export default function SchemaBuilderModal({
  isOpen,
  onClose,
  onSave,
  initialFields = [],
  initialModelName = 'CustomOutput',
  context = 'output',
}) {
  const [modelName, setModelName] = useState('MyDataModel');
  const [fields, setFields] = useState(
    initialFields.length ? initialFields : [createNewField()],
  );
  const [editingField, setEditingField] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setModelName(
        initialModelName ||
          (context === 'globalState' ? 'globalState' : 'CustomOutput'),
      );
      const fieldsToSet =
        initialFields.length > 0 ? initialFields : [createNewField()];
      // For global state, ensure all fields are marked as required
      if (context === 'globalState') {
        setFields(
          produce(fieldsToSet, (draft) => {
            draft.forEach((field) => (field.required = true));
          }),
        );
      } else {
        setFields(fieldsToSet);
      }
    }
  }, [isOpen, initialFields, initialModelName, context]);

  const updateState = (callback) => setFields(produce(callback));

  // FIXED: The logic for adding nested fields is now correct.
  const handleAdd = useCallback((path = null) => {
    updateState((draft) => {
      const newField = createNewField();
      if (!path) {
        draft.push(newField);
        return;
      }

      let target = draft;
      path.split('.').forEach((part) => {
        target = target[isNaN(parseInt(part)) ? part : parseInt(part)];
      });

      if (target && target.children) {
        target.children.push(newField);
      }
    });
  }, []);

  const handleDuplicate = useCallback((path) => {
    updateState((draft) => {
      const copyChildren = (nodes) => {
        return nodes.map((node) => ({
          ...node,
          id: generateId(),
          children: copyChildren(node.children),
        }));
      };

      findAndApply(draft, path, (parent, index) => {
        const sourceField = parent[index];
        const newField = {
          ...sourceField,
          id: generateId(),
          children: copyChildren(sourceField.children),
        };
        parent.splice(index + 1, 0, newField);
      });
    });
  }, []);

  const handleUpdate = useCallback(
    (path, updatedFieldData) => {
      setEditingField((prev) =>
        prev ? { ...prev, data: updatedFieldData } : null,
      );

      updateState((draft) => {
        let fieldToUpdate;
        if (path.includes('.')) {
          findAndApply(draft, path, (parent, key) => {
            fieldToUpdate = parent[key];
          });
        } else {
          fieldToUpdate = draft[path];
        }

        if (
          (fieldToUpdate.dataType === 'Object' ||
            fieldToUpdate.dataType === 'Array') &&
          updatedFieldData.dataType !== 'Object' &&
          updatedFieldData.dataType !== 'Array'
        ) {
          updatedFieldData.children = [];
        }
        if (context === 'globalState') {
          updatedFieldData.required = true;
        }
        Object.assign(fieldToUpdate, updatedFieldData);
      });
    },
    [context],
  );

  const handleDelete = useCallback((path) => {
    updateState((draft) => {
      findAndApply(draft, path, (parent, key) => {
        parent.splice(key, 1);
      });
    });
  }, []);

  // FIX: A robust helper to get a nested array from the state by its path.
  const getNodeByPath = (nodes, path) => {
    if (path === 'root') {
      return nodes;
    }

    const parts = path.split('.');
    let current = nodes;
    for (const part of parts) {
      const key = !isNaN(parseInt(part)) ? parseInt(part) : part;
      if (!current || current[key] === undefined) {
        // This prevents the crash if the path is somehow invalid
        return null;
      }
      current = current[key];
    }
    return current;
  };

  // CRITICAL FIX: Helper to find a field and its path by its unique ID.
  const findFieldById = (nodes, id, currentPath = []) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const path = [...currentPath, i];
      if (node.id === id) {
        return { field: node, path: path.join('.') };
      }
      if (node.children && node.children.length > 0) {
        const result = findFieldById(node.children, id, [...path, 'children']);
        if (result) return result;
      }
    }
    return null;
  };

  const onDragEnd = useCallback(
    (result) => {
      const { source, destination, draggableId } = result;
      if (
        !destination ||
        (source.droppableId === destination.droppableId &&
          source.index === destination.index)
      ) {
        return;
      }
      const draggedItemInfo = findFieldById(fields, draggableId);
      if (!draggedItemInfo) {
        toast.error('Could not find dragged item!');
        return;
      }
      if (destination.droppableId.startsWith(draggedItemInfo.path)) {
        toast.error('Invalid move: cannot drop a field into itself.');
        return;
      }
      updateState((draft) => {
        const sourceList = getNodeByPath(draft, source.droppableId);
        const destList = getNodeByPath(draft, destination.droppableId);
        if (!sourceList || !destList) {
          toast.error('Drag failed: source or destination list not found.');
          return;
        }
        const [movedItem] = sourceList.splice(source.index, 1);
        destList.splice(destination.index, 0, movedItem);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields],
  );

  const handleEdit = useCallback(
    (path) => {
      let fieldData;
      let target = fields;
      path.split('.').forEach((part) => {
        target = target[isNaN(parseInt(part)) ? part : parseInt(part)];
      });
      fieldData = target;
      setEditingField({ path, data: fieldData });
    },
    [fields],
  );

  const handleSaveChanges = () => {
    onSave({ fields, modelName });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="flex h-[85vh] w-full max-w-4xl flex-col rounded-xl border border-slate-700 bg-slate-800/50 shadow-2xl backdrop-blur-xl"
        >
          <div className="flex items-center justify-between border-b border-slate-700 p-4">
            <h1 className="flex items-center gap-3 text-xl font-bold text-slate-100">
              <Settings2 />{' '}
              {context === 'globalState'
                ? 'Global State Builder'
                : 'Output Schema Builder'}
            </h1>
            <div className="flex items-center gap-3">
              {/* --- FIX: ADD FIELD BUTTON --- */}
              <button
                onClick={() => handleAdd()}
                className="flex items-center gap-2 rounded-lg bg-slate-700 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-600"
              >
                <PlusCircle size={16} />
                <span>Add Field</span>
              </button>
              <button
                onClick={handleSaveChanges}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
              >
                <Save size={16} />
                <span>Save and Close</span>
              </button>
            </div>
          </div>
          <div className="flex-grow space-y-6 overflow-y-auto p-6">
            <div>
              <label
                htmlFor="modelName"
                className="mb-2 block text-sm font-medium text-slate-300"
              >
                Model Name
              </label>
              <input
                id="modelName"
                type="text"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                placeholder="MyDataModel"
                className="w-full max-w-sm rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="root" type="field">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="min-h-[200px] space-y-2"
                  >
                    {fields.map((field, index) => (
                      <Draggable
                        key={field.id}
                        draggableId={field.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <FieldRow
                            field={field}
                            path={`${index}`}
                            onDuplicate={handleDuplicate}
                            onDelete={handleDelete}
                            onAddField={handleAdd}
                            onEdit={handleEdit}
                            innerRef={provided.innerRef}
                            draggableProps={provided.draggableProps}
                            dragHandleProps={provided.dragHandleProps}
                            isDragging={snapshot.isDragging}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <FieldEditor
            isOpen={!!editingField}
            onClose={() => setEditingField(null)}
            field={editingField?.data}
            path={editingField?.path}
            onUpdate={handleUpdate}
            context={context}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
